// Per-line artwork asset state — the single source of truth for a quote
// line's uploads, shared by BOTH surfaces that show upload slots:
//   - the inline cover / guts / hardcover slots rendered INSIDE SchemaForm
//     (see SchemaForm.inlineUploadSlots), and
//   - the residual "Artwork for <item>" block (ArtworkUploader) that holds
//     the remaining slots (Hands-on Prep / insert + finishing-driven slots).
//
// Both must reflect the same assets — an upload or delete in one has to show
// in the other immediately. So this state lives ONCE, owned by the per-line
// LineEditor in QuoteEdit and passed to both. (It was previously private to
// ArtworkUploader; it was lifted here verbatim so the two surfaces can share
// one instance rather than each holding a drifting copy.)

import { useQuery } from '@tanstack/react-query';
import i18n from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import {
  isRenderableImage,
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_MB,
  uploadFileTypeRejection,
  type AssetDto,
  type ComponentRole,
  type ReusableAsset,
} from 'shared';

import * as artworkApi from '../api/artwork.js';
import { isAxiosError } from 'axios';
import { extractApiError } from '../api/client.js';
import * as uploadsApi from '../api/uploads.js';
import { detectArtworkInFile, type ArtworkDetection, type ArtworkScanRole } from './detectArtwork.js';
import { detectBarcodeInFile } from './detectBarcode.js';

// Stable empty array so a still-loading library doesn't hand consumers a fresh
// reference every render (the slots memoize on it).
const EMPTY_REUSABLE: readonly ReusableAsset[] = [];

export type PendingUpload = {
  local_id: string;
  file: File;
  preview_url: string | null;
  progress: number;
  error: string | null;
  component_role: ComponentRole;
};

// ── pure presentational helpers (shared with UploadSlot tiles) ───────────
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return 'FILE';
  return filename.slice(dot + 1).toUpperCase().slice(0, 4);
}

// Coarse file-type category that drives the badge / chip color, the way an OS
// file browser tints icons (PDF red, Word blue, …). The colors live in
// styles.css keyed on the data-filetype attribute (no inline hex).
export type FileTypeCategory = 'pdf' | 'word' | 'image' | 'archive' | 'vector' | 'file';
export function fileTypeCategory(filename: string): FileTypeCategory {
  const dot = filename.lastIndexOf('.');
  const ext = dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
  switch (ext) {
    case 'pdf':
      return 'pdf';
    // Word uploads are no longer accepted, but a client-rejected Word file (and
    // any historical Word asset) still renders a tile — keep the blue Word badge
    // so it reads correctly rather than as a generic file.
    case 'doc':
    case 'docx':
      return 'word';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'tif':
    case 'tiff':
    case 'svg':
      return 'image';
    case 'zip':
      return 'archive';
    case 'ai':
    case 'eps':
    case 'ps':
    // Native design files (Photoshop / InDesign) — grouped with the vector
    // badge so they read as "design source", not a generic file. They arrive
    // on artwork slots or the Hands-on Prep catch-all; neither renders a
    // thumbnail (the browser can't rasterize them), so this drives the tile.
    case 'psd':
    case 'indd':
    case 'idml':
      return 'vector';
    default:
      return 'file';
  }
}

// Component roles whose printed FACE plausibly carries the kind of barcode this
// advisory is about — a mailing barcode, a retail UPC, or a tracking code. These
// are the outer surfaces of a piece: a cover, a single-sheet face, the
// "everything in one file" combined upload, packaging, USB body art, printed
// custom packaging (which often carries a UPC or a shipping code). Interior
// manuscript pages ('guts'), endsheets, tabs, inserts, and the specialty/data
// files (emboss / foil / variable_data*) are NOT a barcode surface, so a
// 'missing' scan on one of them must NOT raise the advisory (an interior PDF
// having no barcode is normal, not a heads-up).
const BARCODE_ADVISORY_ROLES: ReadonlySet<ComponentRole> = new Set<ComponentRole>([
  'general',
  'front',
  'back',
  'cover',
  'combined',
  'dust_jacket',
  'packaging',
  'disc_art',
  'custom_packaging_art',
]);

// Whether to show the "we didn't spot a barcode" advisory for the given set of
// uploaded assets. Callers pass the slot's own assets, so the notice renders
// directly under the artwork it scanned (the cover slot, the combined slot, the
// flat single-sheet zone). Among the barcode-relevant assets (see
// BARCODE_ADVISORY_ROLES) show it only when we actually scanned ≥1 file and
// found NO barcode on any of them. A 'found' suppresses it (this artwork has a
// barcode); files that scanned 'unknown' (or predate the feature → null) carry
// no signal and neither trigger nor suppress it. Advisory only — never a submit
// gate.
export function lineHasMissingBarcode(assets: readonly AssetDto[]): boolean {
  let scannedAny = false;
  for (const a of assets) {
    if (!BARCODE_ADVISORY_ROLES.has(a.component_role)) continue;
    if (a.barcode_scan === 'found') return false;
    if (a.barcode_scan === 'missing') scannedAny = true;
  }
  return scannedAny;
}

// ── client-side upload validation (fast-feedback mirror of the backend) ────
//
// Delegates to the ONE shared, role-aware accept policy
// (shared/util/upload-file-types.ts → uploadFileTypeRejection), the same gate
// the backend enforces on every upload — so the two can't drift. This client
// call is just for fast feedback right where the file landed; the backend is
// the authority. Acceptance depends on the SLOT: artwork slots take the print
// formats graphics can receive (PDF, images, AI/PSD/INDD/EPS, zip), the
// manuscript slot ('guts') is PDF-only, and the 'combined' Hands-on Prep
// catch-all takes anything (Word included — we convert it to PDF for you).
export function uploadRejectionReason(file: File, role: ComponentRole): string | null {
  return uploadFileTypeRejection({ role, filename: file.name, content_type: file.type });
}

// Which uploaded roles get their print attributes auto-detected, and the
// detection profile each uses (cover = 1–2 pages, guts = sampled interior,
// general = a flat sheet). A role absent here is never scanned for attributes —
// an endsheet / emboss / foil / variable-data file, or the booklet "combined"
// pile (ambiguous which page is the cover vs the guts), isn't a face we pre-fill
// fields from. See detectArtwork.ts + SchemaForm's detection-consuming effects.
const SCAN_ROLE: Partial<Record<ComponentRole, ArtworkScanRole>> = {
  cover: 'cover',
  guts: 'guts',
  general: 'general',
};

export type LineAssets = {
  assets: AssetDto[];
  pending: PendingUpload[];
  loadingList: boolean;
  bannerError: string | null;
  // Severity of the current bannerError. A failed asset LOAD on a PAST order is
  // expected, not alarming — the original files usually weren't carried over —
  // so it renders as a quiet 'info' note rather than a red 'error' box. Genuine
  // failures (a delete that didn't stick) stay 'error'.
  bannerTone: 'error' | 'info';
  // Latest client-side artwork-attribute detection per role (cover / guts /
  // general), set as each upload's best-effort scan resolves. SchemaForm reads
  // these to PRE-FILL the matching configurator fields (size / color / sides)
  // for the customer to confirm — never a price input (CLAUDE.md §"Pricing logic
  // lives in the ERP"). A role with no entry simply hasn't been scanned.
  detections: Partial<Record<ComponentRole, ArtworkDetection>>;
  handleFiles: (files: ArrayLike<File> | null, role: ComponentRole) => void;
  handleDelete: (asset: AssetDto) => void;
  dismissPending: (local_id: string) => void;
  // Files the caller's Company has already sent us, offered in the upload slots
  // so a repeat order can point at one instead of re-uploading it. Company-wide
  // (not per line), so every line editor on the quote shares ONE fetch via the
  // query key. Empty when there's nothing on record, no matched Company, or the
  // Nexus lookup degraded — the picker simply doesn't appear.
  reusable: readonly ReusableAsset[];
  // Attach a library file to this line's slot. Records a new Asset row against
  // the SAME content-addressed blob — no byte re-upload — so the file behaves
  // exactly like a fresh one from here on (thumbnail, upload gate, and the
  // submit manifest, which carries the object_key verbatim to Nexus).
  attachReusable: (asset: ReusableAsset, role: ComponentRole) => Promise<void>;
};

export function useLineAssets(args: {
  quote_id: string;
  // When set, scope to a single QuoteLine — only assets with matching
  // quote_line_id are listed, and new uploads are tagged with this line.
  // Undefined → quote-level scoping (drafts before the per-line UI).
  quote_line_id?: string | null;
  // Disable mutations (quote locked / paid / cancelled).
  disabled?: boolean;
  // Past-order (M2M historical clone) context. A failed asset LOAD is then
  // expected — the original files typically weren't migrated — so we soften the
  // message and render it as a quiet info note, not a red error box.
  isHistorical?: boolean;
  // Fired after every upload / delete so the parent can refresh its
  // "ready to submit" state (e.g. the step-8 variable-data pre-block).
  onAssetsChanged?: () => void;
}): LineAssets {
  const { quote_id, quote_line_id, disabled = false, isHistorical = false, onAssetsChanged } = args;
  const [assets, setAssets] = useState<AssetDto[]>([]);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [bannerTone, setBannerTone] = useState<'error' | 'info'>('error');
  const [detections, setDetections] = useState<Partial<Record<ComponentRole, ArtworkDetection>>>({});

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    uploadsApi
      .listAssets(quote_id)
      .then((rows) => {
        if (cancelled) return;
        // Per-line scoping when quote_line_id is set: only show assets
        // tagged to this line. Undefined → keep the legacy "all quote
        // assets" behavior so callers that haven't migrated still work.
        const scoped =
          quote_line_id != null ? rows.filter((r) => r.quote_line_id === quote_line_id) : rows;
        setAssets(scoped);
      })
      .catch(() => {
        if (cancelled) return;
        if (isHistorical) {
          // A past order's files usually weren't carried over from the original —
          // this isn't a failure the customer needs to worry about, just a nudge
          // to re-attach artwork before resubmitting.
          setBannerTone('info');
          setBannerError('Your original files weren’t carried over. Please re-upload any artwork before you resubmit.');
        } else {
          setBannerTone('error');
          setBannerError('We couldn’t load your uploaded files.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quote_id, quote_line_id, isHistorical]);

  useEffect(() => {
    return () => {
      for (const p of pending) {
        if (p.preview_url) URL.revokeObjectURL(p.preview_url);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startUpload = useCallback(
    async (file: File, role: ComponentRole): Promise<void> => {
      // Validate against the backend allow-list + size cap BEFORE starting an
      // upload. A rejected file renders as an inline tile in the slot it was
      // dropped on — carrying the reason + a Dismiss action, the same shape a
      // failed upload uses — so a Word doc on the manuscript slot is refused
      // right where it landed, with guidance, instead of after a round-trip.
      const rejection =
        uploadRejectionReason(file, role) ??
        (file.size > MAX_UPLOAD_BYTES
          ? i18n.t('quote:artwork.overUploadLimit', { name: file.name, size: MAX_UPLOAD_MB })
          : null);
      if (rejection) {
        const rejected_id = `rejected_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        setPending((cur) => [
          ...cur,
          {
            local_id: rejected_id,
            file,
            preview_url: null,
            progress: 0,
            error: rejection,
            component_role: role,
          },
        ]);
        return;
      }
      const local_id = `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const preview_url = isRenderableImage(file.type) ? URL.createObjectURL(file) : null;
      setPending((cur) => [
        ...cur,
        { local_id, file, preview_url, progress: 0, error: null, component_role: role },
      ]);
      // Kick off the barcode-presence scan NOW so it runs in parallel with the
      // upload PUT (uploadFile awaits it only at register time). Advisory only —
      // a 'missing' result later surfaces a non-blocking notice; it never gates
      // the upload, and detectBarcodeInFile resolves 'unknown' rather than throw.
      const barcode_scan_promise = detectBarcodeInFile(file);
      // Kick off artwork-attribute detection in parallel too — best-effort, and
      // never blocks or alters the upload. When it resolves we store the latest
      // per-role result; SchemaForm consumes it to pre-fill the matching fields
      // (size / color / sides) for the customer to confirm. Skipped for roles
      // that aren't a printable face we pre-fill from (SCAN_ROLE). detectArtwork
      // never throws — it resolves null rather than reject — so this is fire-and
      // -forget alongside the PUT.
      const scanRole = SCAN_ROLE[role];
      if (scanRole) {
        void detectArtworkInFile(file, scanRole).then((result) => {
          if (result) setDetections((cur) => ({ ...cur, [role]: result }));
        });
      }
      try {
        const asset = await uploadsApi.uploadFile({
          file,
          quote_id,
          quote_line_id: quote_line_id ?? null,
          component_role: role,
          barcode_scan_promise,
          onProgress: (fraction) => {
            setPending((cur) =>
              cur.map((p) => (p.local_id === local_id ? { ...p, progress: fraction } : p)),
            );
          },
        });
        setPending((cur) => {
          const dropped = cur.find((p) => p.local_id === local_id);
          if (dropped?.preview_url) URL.revokeObjectURL(dropped.preview_url);
          return cur.filter((p) => p.local_id !== local_id);
        });
        setAssets((cur) => [asset, ...cur]);
        onAssetsChanged?.();
      } catch (err) {
        const envelope = extractApiError(err);
        const message =
          envelope?.message ??
          (err instanceof Error && !isAxiosError(err) ? err.message : 'Upload failed. Please try again.');
        setPending((cur) =>
          cur.map((p) => (p.local_id === local_id ? { ...p, error: message, progress: 0 } : p)),
        );
      }
    },
    [quote_id, quote_line_id, onAssetsChanged],
  );

  const handleFiles = useCallback(
    (files: ArrayLike<File> | null, role: ComponentRole): void => {
      if (!files || disabled) return;
      setBannerError(null);
      for (const f of Array.from(files)) void startUpload(f, role);
    },
    [disabled, startUpload],
  );

  const handleDelete = useCallback(
    async (asset: AssetDto): Promise<void> => {
      if (disabled) return;
      setBannerError(null);
      setAssets((cur) => cur.filter((a) => a.id !== asset.id));
      try {
        const { retained_elsewhere } = await uploadsApi.deleteAsset(asset.id);
        // The same file is still attached to other work of theirs, so we
        // detached it here instead of destroying it. Say so — otherwise
        // "Remove" reads as "delete everywhere" and a customer who reused a
        // file across orders has no way to know their other order is safe.
        if (retained_elsewhere) {
          setBannerTone('info');
          setBannerError(i18n.t('quote:artwork.removedKeptOnFile'));
        }
        onAssetsChanged?.();
      } catch {
        setAssets((cur) => [asset, ...cur]);
        setBannerTone('error');
        setBannerError('We couldn’t remove that file. Please try again.');
      }
    },
    [disabled, onAssetsChanged],
  );

  // The Company's reusable-artwork library. One shared query key, so N line
  // editors on a quote make ONE request. Non-price company data, so caching is
  // permitted (CLAUDE.md §Caching) — same posture as the kit-BOM picker.
  const { data: reusableData } = useQuery({
    queryKey: ['reusable-artwork'],
    queryFn: artworkApi.getReusableArtwork,
    enabled: !disabled,
    staleTime: 5 * 60 * 1000,
  });

  const attachReusable = useCallback(
    async (asset: ReusableAsset, role: ComponentRole): Promise<void> => {
      if (disabled) return;
      setBannerError(null);
      try {
        const created = await artworkApi.reuseArtwork({
          quote_id,
          quote_line_id: quote_line_id ?? null,
          component_role: role,
          object_key: asset.object_key,
        });
        setAssets((cur) => [created, ...cur]);
        onAssetsChanged?.();
      } catch (err) {
        const envelope = extractApiError(err);
        setBannerTone('error');
        setBannerError(
          envelope?.message ?? 'We couldn’t reuse that file. Please upload it instead.',
        );
      }
    },
    [disabled, quote_id, quote_line_id, onAssetsChanged],
  );

  const dismissPending = useCallback((local_id: string): void => {
    setPending((cur) => {
      const dropped = cur.find((p) => p.local_id === local_id);
      if (dropped?.preview_url) URL.revokeObjectURL(dropped.preview_url);
      return cur.filter((p) => p.local_id !== local_id);
    });
  }, []);

  return {
    assets,
    pending,
    loadingList,
    bannerError,
    bannerTone,
    detections,
    handleFiles,
    handleDelete,
    dismissPending,
    reusable: reusableData ?? EMPTY_REUSABLE,
    attachReusable,
  };
}
